import { ExtendedColumnType, useColumnExpansion } from "src/pages/binOverview/BinCommander";
import dayjs, { Dayjs } from "dayjs";
import React, { useMemo } from "react";
import { Table, Typography } from "antd";
import { useGetPremierPlusAlertsQuery } from "src/queries/useGetPremierPlusAlertsQuery";
import AlertDTO from "src/models/AlertDTO";
import "./table.css";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import EventDTO from "src/models/EventDTO";

interface PremierPlusTapestripTableProps{
  binId: number;
  dateRange: [Dayjs | null, Dayjs | null];
  alertFilter?: 1 | 2; // 0: both, 1: active only, 2: inactive only
  binDto: PremierPlusBinDTO | null | undefined;
}

const { Text } = Typography;

const PremierPlusAlertTable: React.FC<PremierPlusTapestripTableProps> = ({ binId, dateRange, alertFilter, binDto }) => {
  const start = dateRange[0]?.startOf('day'); 
  const end = dateRange[1]?.endOf('day');   

  const startIso = start?.toISOString() ?? null;
  const endIso = end?.toISOString() ?? null;

  const { getPremierPlusAlertsQuery } = useGetPremierPlusAlertsQuery(
    binId,
    startIso,
    endIso,
    { enabled: true, refetchOnWindowFocus: false }
  );

  const alerts = useMemo(() => {
    if(alertFilter === 1){
      return binDto?.activeAlerts ?? [];
    }
    else if(alertFilter === 2){
      return getPremierPlusAlertsQuery.data ?? [];
    }else{
      return [...binDto?.activeAlerts ?? [], ...getPremierPlusAlertsQuery.data ?? []];
    }

  }, [getPremierPlusAlertsQuery.data, start, end, alertFilter]);

  const columns: Array<ExtendedColumnType<EventDTO>> = [
    {
      title: "Trigger Time",
      dataIndex: "triggerTimeUtc",
      sorter: (a, b) => dayjs(a.triggerTimeUtc).diff(b.triggerTimeUtc),
      render(value) {
        return <span>{dayjs(value).format("hh:mm A MM/DD/YYYY")}</span>;
      },
      width: "176px",
      sortType: "date",
      defaultSortOrder: "descend",
    },
    {
      title: "Alert Code",
      dataIndex: "alertCodeString",
      width: "100px",
      render(value) {
        return <Text strong>{value}</Text>;
      },
    },
    {
      title: "Title",
      dataIndex: "title",
      width: "200px",
      render(value) {
        return <Text type="secondary">{value}</Text>;
      },
    },
    {
      title: "Message",
      dataIndex: "description",
      render(value) {
        return <Text>{value}</Text>;
      },
    },
    {
      title: "Resolve Time",
      dataIndex: "resolveTimeUtc",
      sorter: (a, b) => {
        const ax = a.resolveTimeUtc ? dayjs(a.resolveTimeUtc) : dayjs(0);
        const bx = b.resolveTimeUtc ? dayjs(b.resolveTimeUtc) : dayjs(0);
        return ax.diff(bx);
      },
      render(value) {
        return value == null ? "Not resolved" : <span>{dayjs(value).format("hh:mm A MM/DD/YYYY")}</span>;
      },
      width: "176px",
      sortType: "date",
    },
  ];

  const transformedColumns = useColumnExpansion({ columns, datasource: alerts });

  return (
    <Table
      sticky
      size="small"
      pagination={{ defaultPageSize: 10, size: "default" }}
      dataSource={alerts}
      columns={transformedColumns}
      rowClassName={(record: EventDTO) => (record.resolveTimeUtc == null ? "unresolved-row" : "resolved-row")}
    />
  );
};

export default PremierPlusAlertTable;
